import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router, ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Subject, takeUntil } from 'rxjs';
import {
  AnalyticsService,
  CLIENT_EVENTS,
  GA4_EVENTS,
} from 'src/app/shared/analytics';
import { analyticsDistinctId, rawIdFromUserInfo } from 'src/app/shared/analytics/identity';

type LoginMode = 'otp' | 'password';
type OtpStep = 'identifier' | 'otp';

@Component({
  selector: 'app-customer-signin',
  templateUrl: './customer-signin.component.html',
  styleUrls: ['./customer-signin.component.scss']
})
export class CustomerSigninComponent implements OnInit, OnDestroy {
  loginMode: LoginMode = 'otp';

  otpStep: OtpStep = 'identifier';
  identifier = '';
  identifierError = '';
  otpCode = '';
  otpError = '';
  otpIsEmail = false;
  maskedIdentifier = '';
  otpTimer = 0;
  private otpInterval: any;

  loginForm: FormGroup;
  showPassword = false;
  isLoading = false;

  private destroy$ = new Subject<void>();
  private apiBaseUrl = environment.apiUrl;

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private router: Router,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private analytics: AnalyticsService,
  ) {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    this.checkGoogleCallback();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearOtpTimer();
  }

  setLoginMode(mode: LoginMode): void {
    if (this.loginMode === mode) return;
    this.loginMode = mode;
    if (mode === 'otp') {
      this.otpStep = 'identifier';
      this.identifier = '';
      this.otpCode = '';
      this.identifierError = '';
      this.otpError = '';
    }
  }

  private readonly emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  private readonly phoneRegex = /^[6-9]\d{9}$/;

  get isValidIdentifier(): boolean {
    const val = this.identifier.trim();
    if (!val) return false;
    if (this.looksLikeEmail(val)) {
      return this.emailRegex.test(val);
    }
    return this.phoneRegex.test(val.replace(/\D/g, ''));
  }

  private looksLikeEmail(val: string): boolean {
    return val.includes('@') || /[a-zA-Z]/.test(val);
  }

  validateIdentifier(): void {
    const val = this.identifier.trim();
    if (!val) {
      this.identifierError = '';
      return;
    }
    if (this.looksLikeEmail(val)) {
      this.identifierError = this.emailRegex.test(val)
        ? ''
        : 'Please enter a valid email address (e.g. name@company.com).';
      return;
    }
    const digits = val.replace(/\D/g, '');
    if (digits.length !== 10) {
      this.identifierError = 'Mobile number must be exactly 10 digits.';
      return;
    }
    if (!this.phoneRegex.test(digits)) {
      this.identifierError = 'Mobile number must start with 6, 7, 8 or 9.';
      return;
    }
    this.identifierError = '';
  }

  onIdentifierChange(): void {
    if (this.identifierError) this.identifierError = '';
  }

  sendOtp(): void {
    const val = this.identifier.trim();
    if (!this.isValidIdentifier) {
      this.identifierError = val.includes('@')
        ? 'Please enter a valid email address.'
        : 'Please enter a valid 10-digit mobile number.';
      return;
    }

    this.identifierError = '';
    this.isLoading = true;
    this.otpIsEmail = val.includes('@');

    const isEmail = val.includes('@');
    const url = isEmail
      ? `${this.apiBaseUrl}customer/email/login/send-otp`
      : `${this.apiBaseUrl}customer/phone/login/send-otp`;
    const payload = isEmail
      ? { email: val }
      : { phoneNumber: val.replace(/\D/g, '') };

    this.http.post<any>(url, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          if (response?.success) {
            this.maskedIdentifier = this.buildMaskedIdentifier(val, isEmail);
            this.otpStep = 'otp';
            this.startOtpTimer();
            this.showSuccess(isEmail ? 'OTP sent to your email!' : 'OTP sent successfully!');
          } else {
            this.identifierError = response?.message || 'Failed to send OTP';
          }
        },
        error: (error) => {
          this.isLoading = false;
          this.identifierError = this.extractErrorMessage(error, 'Failed to send OTP');

          if (error.status === 404 && error.error?.code === 'ACCOUNT_NOT_FOUND') {
            this.identifierError = 'Account not found. Please sign up first.';
            setTimeout(() => {
              this.router.navigate(['/customers/signup']);
            }, 3000);
          }
        }
      });
  }

  verifyOtp(): void {
    if (!this.otpCode || this.otpCode.length !== 6) {
      this.otpError = 'Please enter the 6-digit OTP.';
      return;
    }

    this.otpError = '';
    this.isLoading = true;

    const val = this.identifier.trim();
    const isEmail = val.includes('@');
    const url = isEmail
      ? `${this.apiBaseUrl}customer/email/login/verify-otp`
      : `${this.apiBaseUrl}customer/phone/login/verify-otp`;
    const payload = isEmail
      ? { email: val, otp: this.otpCode }
      : { phoneNumber: val.replace(/\D/g, ''), otp: this.otpCode };

    this.http.post<any>(url, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          if (response?.success) {
            this.handleOtpLoginSuccess(response, isEmail);
          } else {
            this.otpError = response?.message || 'Invalid OTP';
          }
        },
        error: (error) => {
          this.isLoading = false;
          this.otpError = this.extractErrorMessage(error, 'Invalid OTP. Please try again.');
        }
      });
  }

  resendOtp(): void {
    if (this.otpTimer > 0 || this.isLoading) return;
    this.otpCode = '';
    this.otpError = '';
    this.sendOtp();
  }

  goBackToIdentifier(): void {
    this.otpStep = 'identifier';
    this.otpCode = '';
    this.otpError = '';
    this.clearOtpTimer();
  }

  formatOtpInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/\D/g, '').slice(0, 6);
    this.otpCode = input.value;
  }

  private buildMaskedIdentifier(val: string, isEmail: boolean): string {
    if (isEmail) {
      const [name, domain] = val.split('@');
      if (!name || !domain) return val;
      const visible = name.slice(0, Math.min(2, name.length));
      return `${visible}${'*'.repeat(Math.max(name.length - 2, 2))}@${domain}`;
    }
    const digits = val.replace(/\D/g, '');
    if (digits.length < 4) return digits;
    return `******${digits.slice(-4)}`;
  }

  private startOtpTimer(): void {
    this.clearOtpTimer();
    this.otpTimer = 60;
    this.otpInterval = setInterval(() => {
      this.otpTimer--;
      if (this.otpTimer <= 0) this.clearOtpTimer();
    }, 1000);
  }

  private clearOtpTimer(): void {
    if (this.otpInterval) {
      clearInterval(this.otpInterval);
      this.otpInterval = null;
    }
    this.otpTimer = 0;
  }

  private handleOtpLoginSuccess(response: any, isEmail: boolean): void {
    const token = response.access_token;
    const user = response.user || {};

    if (!token) {
      this.otpError = 'Authentication token missing';
      return;
    }

    localStorage.setItem('accessToken', token);
    localStorage.setItem('tokenType', response.token_type || 'Bearer');
    localStorage.setItem('tokenExpiry', (Date.now() + (response.expires_in || 86400) * 1000).toString());
    localStorage.setItem('authType', isEmail ? 'email_otp' : 'phone_otp');

    const userInfo = {
      id: user.external_id,
      email: user.email,
      name: user.name,
      'custom:team': user.team,
      'custom:visibility': user.user_type || 'customer',
      company: user.company,
      phone_number: user.phone_number || (isEmail ? '' : this.identifier.replace(/\D/g, '')),
      phone_number_verified: !isEmail,
      auth_type: isEmail ? 'email_otp' : 'phone_otp',
      profile_complete: user.profile_complete || false
    };

    localStorage.setItem('userInfo', JSON.stringify(userInfo));
    this.routeAfterLogin(userInfo, 'Login successful!');
  }

  onPasswordSubmit(): void {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }
    this.isLoading = true;

    this.http.post<any>(`${this.apiBaseUrl}customer/login`, {
      email: this.loginForm.value.email,
      password: this.loginForm.value.password,
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        this.isLoading = false;
        const token = response.access_token;
        const user = response.user || {};

        if (!token) {
          this.showError('Authentication token missing');
          return;
        }

        localStorage.setItem('accessToken', token);
        localStorage.setItem('tokenType', response.token_type || 'Bearer');
        localStorage.setItem('tokenExpiry', (Date.now() + (response.expires_in || 3600) * 1000).toString());
        localStorage.setItem('authType', 'email');

        const userInfo = {
          id: user.external_id,
          email: user.email,
          name: user.name,
          'custom:team': user.team,
          'custom:visibility': user.user_type || 'customer',
          company: user.company,
          phone_number: user.customer_number || '',
          phone_number_verified: false,
          auth_type: 'email'
        };

        localStorage.setItem('userInfo', JSON.stringify(userInfo));
        this.routeAfterLogin(userInfo, 'Login successful!');
      },
      error: (error) => {
        this.isLoading = false;
        this.showError(this.extractErrorMessage(error, 'Invalid Email or Password.'));
      }
    });
  }

  signInWithGoogle(): void {
    this.isLoading = true;
    window.location.href = `${this.apiBaseUrl}auth/google`;
  }

  private checkGoogleCallback(): void {
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      if (params['access_token']) {
        this.processGoogleCallback(params);
      }
    });
  }

  private processGoogleCallback(params: any): void {
    const token = params['access_token'];
    if (token) {
      localStorage.setItem('accessToken', token);
      localStorage.setItem('tokenType', params['token_type'] || 'bearer');
      localStorage.setItem('tokenExpiry', (Date.now() + (parseInt(params['expires_in']) || 86400) * 1000).toString());
      localStorage.setItem('authType', 'google');

      if (params['user']) {
        try {
          const user = JSON.parse(atob(params['user']));
          localStorage.setItem('userInfo', JSON.stringify(user));
        } catch (e) {
          console.error('Failed to parse user data', e);
        }
      }

      this.routeAfterLogin(null, 'Google login successful!');
    }
    this.isLoading = false;
    this.clearUrlParameters();
  }

  private routeAfterLogin(userInfo: any, successMessage: string): void {
    this.showSuccess(successMessage);

    try {
      const ui = userInfo || JSON.parse(localStorage.getItem('userInfo') || 'null');
      const authType = localStorage.getItem('authType') || ui?.auth_type || 'unknown';
      if (ui && (ui.id || ui.sub)) {
        const distinctId = analyticsDistinctId('customer', rawIdFromUserInfo('customer', ui));
        this.analytics.identify({
          distinctId: distinctId ?? '',
          role: 'customer',
          properties: {
            email: ui.email,
            name: ui.name,
            phone: ui.phone_number,
            company_name: ui.company,
          },
        });
        this.analytics.track(GA4_EVENTS.LOGIN, { method: authType, role: 'customer' });
        this.analytics.track(CLIENT_EVENTS.CLIENT_LOGGED_IN, { method: authType });
      }
    } catch {
    }

    const profileComplete = userInfo?.profile_complete !== false;

    if (!profileComplete && localStorage.getItem('authType') === 'phone_otp') {
      this.router.navigate(['/customer/complete-profile']);
    } else {
      const redirectUrl = localStorage.getItem('redirectAfterLogin');
      if (redirectUrl) {
        localStorage.removeItem('redirectAfterLogin');
        this.router.navigateByUrl(redirectUrl);
      } else {
        this.router.navigate(['/workspace/customer']);
      }
    }
  }

  private clearUrlParameters(): void {
    window.history.replaceState({}, document.title, window.location.pathname);
  }

  private extractErrorMessage(error: any, fallback: string): string {
    return error?.error?.message || error?.error?.detail || fallback;
  }

  private showSuccess(message: string): void {
    this.snackBar.open(message, 'Close', { duration: 3000 });
  }

  private showError(message: string): void {
    this.snackBar.open(message, 'Close', { duration: 5000 });
  }
}
